import { Instance, types, getRoot, cast } from "mobx-state-tree";
import { RootInstance, rootStore } from "./root";
import { CSInterface } from "csinterface-ts";
import { jsxConstFunctions } from "../jsxProviders/jsxConsts";
import { analyticsInstance } from "../analytics/analyticsCtrls";
import {
  asyncOpStateType,
  updateAsyncStatus,
  AsyncOpState,
  updateSaveCompStep,
  SaveCompStep,
  saveCompProcessStepType,
} from "./utils/async-op-state";
import { fixFileName } from "./utils/fixFileName";
import { uploadAssets, uploadAssetsPromises } from "./renderStore/uploadAssets";
import { uploadFonts } from "./renderStore/uploadFonts";
import { getAssets } from "./renderStore/getAssets";
import { uploadJsonToVideoPart } from "./renderStore/uploadJsonToVideoPart";
import { encodeAssets } from "./renderStore/encodeAssets";
import { getFontWeight } from "./utils/fontFaceFileGenerator";
import packageJson from "../../package.json";
import { checkUserCancel } from "./utils/checkUserCancel";
import { uploadFileToS3 } from "./utils/uploadFileToS3";
import { getProject } from "../jsxProviders/scripts/jsxTools";
import { getServerUrl } from "../utils/serverUrl";

const { csInterface }: { csInterface: CSInterface } = window as any;
export const RenderStore = types
  .model({
    jsonFileName: types.optional(types.string, ""),
    isRendering: types.optional(types.boolean, false),
    saveCompStatus: asyncOpStateType,
    currentStepProgress: types.optional(types.number, 0),
    saveCompStep: saveCompProcessStepType,
    compImages: types.frozen(),
    compVideos: types.frozen(),
    imagesToUpload: types.array(
      types.model({
        name: types.string,
        url: types.string,
        blob: types.frozen(),
      })
    ),
    uploadedImages: types.array(
      types.model({
        name: types.string,
        url: types.string,
      })
    ),
    uploadedFonts: types.array(
      types.model({
        url: types.string,
        family: types.string,
        style: types.string,
        weight: types.string,
      })
    ),
    hasVideo: types.optional(types.boolean, false),
    compJson: types.frozen(),
    isCancelling: types.optional(types.boolean, false),
    renderedAssetsTypes: types.optional(types.array(types.string), []),
    doNotAskFileSize: types.optional(types.boolean, false),
    ignoreProblemLayers: types.optional(types.boolean, false),
    filesFolderPath: types.optional(types.string, ""),
    passedRenderSteps: types.optional(types.array(types.string), []),
    processIsCancelled: types.optional(types.boolean, false),
    beforeRenderState: types.frozen(),
  })
  .views((self) => ({
    get platformStorageProjectAssetsRoot(): string {
      return `projects/${
        getRoot<RootInstance>(self).projectStore.selectedProjectId
      }/assets`;
    },
  }))
  .actions((self) => ({
    updateSaveCompStatus: updateAsyncStatus(self, "saveCompStatus"),
    updateSaveCompStepStatus: updateSaveCompStep(self, "saveCompStep"),
    setUploadedImages(name: string, url: string) {
      self.uploadedImages.push({ name, url });
    },
    setDoNotAskFileSize(bool: boolean) {
      self.doNotAskFileSize = bool;
    },
    setIgnoreProblemLayers(bool: boolean) {
      self.ignoreProblemLayers = bool;
    },
    clearUploadedImages() {
      self.uploadedImages.length = 0;
    },
    clearImagesToUpload() {
      self.imagesToUpload.length = 0;
    },
    setCompJson(json: any) {
      self.compJson = { ...json, extensionVersion: packageJson.version };
    },
    setIsRendering(bool: boolean) {
      self.isRendering = bool;
    },
    setRenderedAssetsTypes(types: string[]) {
      self.renderedAssetsTypes = cast(types);
    },
    setIsCancelling(cancelling: boolean) {
      self.isCancelling = cancelling;
    },
    setHasVideo(bool: boolean = true) {
      self.hasVideo = bool;
    },
    setUploadedFonts(
      fonts: {
        url: string;
        family: string;
        style: string;
        name: string;
        weight: string;
        isVariableFont: boolean;
      }[]
    ) {
      const fontsWithWeight = fonts.map((font) => {
        return {
          ...font,
          name: undefined,
          isVariableFont: undefined,
        };
      });
      self.uploadedFonts.clear();
      self.uploadedFonts.push(...fontsWithWeight);
    },
    getFullUrlOfAsset(assetOriginalName: string, folder: string) {
      return `${self.platformStorageProjectAssetsRoot}/${folder}/${assetOriginalName}`;
    },
    setFilesFolderPath(path: string) {
      self.filesFolderPath = path;
    },
    addToCompleteRenderSteps(step: string) {
      self.passedRenderSteps.push(step);
    },
    clearCompleteRenderSteps() {
      self.passedRenderSteps.replace([]);
    },
    setProcessIsCancelled(flag: boolean) {
      self.processIsCancelled = flag;
      if (flag) {
        analyticsInstance.sendAnalytics("render", {
          val: "cancel",
          sceneName:
            getRoot<RootInstance>(self).compositionStore
              .compositionCurrentlyRendering?.name,
        });
        csInterface.evalScript(jsxConstFunctions.cancelRender, (res) => {
          console.log("Cancelling");
        });
      }
    },
    setBeforeRenderState(state: any) {
      self.beforeRenderState = { ...state };
    },
    setCurrentCompStepProgress(progress: number) {
      self.currentStepProgress = progress;
    },
  }))
  .actions((self) => ({
    async cancelRender() {
      if (
        self.saveCompStep !== SaveCompStep.Idle &&
        self.saveCompStep !== SaveCompStep.Rendering &&
        self.saveCompStep !== SaveCompStep.CreatingScene
      ) {
        // If we are in the uploading step, only cancel after await is done
        self.setIsCancelling(true);
        if (uploadAssetsPromises?.length) {
          //TODO
          // for (let asset of uploadAssetsPromises) {
          //   try {
          //     await Storage.cancel(asset);
          //   } catch (e) {
          //     console.log("cancelling upload", e);
          //   }
          // }
        }
      } else {
        self.setProcessIsCancelled(true);
      }
    },
  }))
  .actions((self) => ({
    async uploadProject() {
      const projectPath: any = await getProject();
      const response = await fetch(getServerUrl('project-file-data'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filePath: projectPath.filePath }),
      });

      if (!response.ok) {
        console.error("Failed to retrieve project file from server");
        return;
      }
      function base64ToFile(
        base64: string,
        filename: string,
        mimeType = "application/octet-stream"
      ) {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        return new File([blob], filename, { type: mimeType });
      }
      const json = await response.json();

      // Extract the original file name from the path
      const originalFileName = projectPath.filePath.split("/").pop();

      // Generate a timestamp (up to the minute)
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[-:T]/g, "");

      // Construct the new file name with the timestamp
      const newFileName = `${timestamp}_${originalFileName as string}`;

      // Convert the base64 data into a File object
      const file = base64ToFile(
        json.data,
        newFileName,
        "application/octet-stream"
      );

      // Construct the S3 key with the updated file name
      const s3Key = `projects/${
        getRoot<RootInstance>(self).projectStore.selectedProjectId
      }/assets/aep/${newFileName}`;
      await uploadFileToS3(
        s3Key,
        {
          file: file,
        },
        "application/octet-stream"
      );
    },
  }))
  .actions((self) => ({
    async uploadAllAssets(videoPartName: string) {
      self.jsonFileName =
        fixFileName(
          getRoot<RootInstance>(self).compositionStore
            .compositionCurrentlyRendering?.name
        ) + ".json";
      try {
        console.log(`uploading to scene ${videoPartName}`);
        const assetsToEncode = await this.getAssets(self);
        self.setRenderedAssetsTypes([...Object.keys(assetsToEncode), "json"]);
        const { videos, images, audio, fonts } = assetsToEncode;
        const videoData = videos ? await encodeAssets(videos, "video") : null;
        const imageData = images ? await encodeAssets(images, "image") : null;
        const audioData = audio ? await encodeAssets(audio, "audio") : null;
        if (
          (await rootStore.authStore.hasBlingsAccount()) &&
          rootStore.compositionStore.renderedCompositionsSuccess.length === 0
        ) {
          //only upload project once. At first success in cases of cancelling or fail render 
          await self.uploadProject();
        }
        const progressCallback = (progressLoaded: number) =>
          self.setCurrentCompStepProgress(progressLoaded);
        if (imageData) {
          self.updateSaveCompStepStatus(SaveCompStep.UploadingImages);
          await checkUserCancel(() =>
            this.uploadAssets(self, imageData, "image", progressCallback)
          );
          self.addToCompleteRenderSteps("UploadingImages");
        }
        if (videoData) {
          self.updateSaveCompStepStatus(SaveCompStep.UploadingVideos);
          await checkUserCancel(() =>
            this.uploadAssets(self, videoData, "video", progressCallback)
          );
          self.setHasVideo();
          self.addToCompleteRenderSteps("UploadingVideos");
        }
        if (audioData) {
          self.updateSaveCompStepStatus(SaveCompStep.UploadingAudio);
          await checkUserCancel(() =>
            this.uploadAssets(self, audioData, "audio", progressCallback)
          );
          self.addToCompleteRenderSteps("UploadingAudio");
        }

        if (fonts) {
          self.updateSaveCompStepStatus(SaveCompStep.UploadingFonts);
          await checkUserCancel(() => this.uploadFonts(self, fonts));
          self.addToCompleteRenderSteps("UploadingFonts");
        }

        self.updateSaveCompStepStatus(SaveCompStep.UploadingJson);
        await checkUserCancel(() =>
          this.uploadJsonToVideoPart(videoPartName, self)
        );
        self.updateSaveCompStepStatus(SaveCompStep.Idle);
        self.clearCompleteRenderSteps();
      } catch (e: any) {
        console.error("err", e);
        self.updateSaveCompStatus(AsyncOpState.Error);
        self.clearCompleteRenderSteps();
        analyticsInstance.sendAnalytics("render", {
          val: "fail",
          m: e?.message,
          sceneName: videoPartName,
        });
      }
    },
    cancelProcess() {
      self.setDoNotAskFileSize(false);
      self.setHasVideo(false);
      rootStore.uiStore.setRenderProgress(0);
      self.setCurrentCompStepProgress(0);
      self.updateSaveCompStepStatus(SaveCompStep.Idle);
      self.clearCompleteRenderSteps();
      rootStore.uiStore.setRenderProcessHidden();
      rootStore.compositionStore.setRenderedCompositions(null);
      rootStore.compositionStore.setQueuedCompositions(null);
      self.setIsRendering(false);
    },
    uploadJsonToVideoPart: uploadJsonToVideoPart,
    uploadFonts: uploadFonts,
    getAssets: getAssets,
    uploadAssets: uploadAssets,
  }));

export type IRenderModel = Instance<typeof RenderStore>;
